// src/_Hooks/_Context/useDataUserAuthenticated.js

import { useJwt } from "react-jwt";
import { ApiConsume } from "../../_API/ApiConsume";
import { createContext, useState, useEffect, useCallback, useMemo } from "react";
import { useNotifications } from 'reapop';
import { safeGetAuthToken } from "../../Helpers/authStorage";
import useAddonStatus from "../useAddonStatus";

export const UseDataUserContext = createContext();

export const UseDataUserProvider = ({ children }) => {
    const authState = safeGetAuthToken();
    const token = authState?.token || '';
    const isLogged = !!token;

    const { notify } = useNotifications();
    // 2) Decodificamos el token para extraer datos, p. ej. user_id, is_associated
    const { decodedToken } = useJwt(token);
    const decodedRole = (decodedToken?.role || '').toUpperCase();
    const storedRole = (authState?.metadata?.role || '').toUpperCase();
    const effectiveRole = decodedRole || storedRole || '';
    const isOwnerRole = effectiveRole === 'PROPIETARIO';

    // 3) Estado principal de la info del usuario logueado (o del principal)
    const [useInfoUserAuth, setInfoUserAuth] = useState({
        isLoading: true,
        data: {
            id: 0,
            first_name: "",
            last_name: "",
            domicilio: "",
            telefono: "",
            cuit: "",
            provincia: "",
            codigo_postal: "",
            afip: "",
            username: "",
            email: "",
            role: "",
            numero_inscripcion_rca_rcpa: "",
            telefono_urgencias: "",
            token: null,
            // <-- Nuevo campo
            is_superuser: false,
        }
    });

    // 4) Estado para actualizar perfil
    const [useInputProfileUserValue, setInputProfileUserValue] = useState({
        first_name: "",
        last_name: "",
        domicilio: "",
        telefono: "",
        cuit: "",
        provincia: "",
        codigo_postal: "",
        afip: "",
        username: "",
        email: "",
        role: "",
        numero_inscripcion_rca_rcpa: "",
        telefono_urgencias: ""
    });

    // Capacidades oficiales del backend (integración progresiva).
    const [capabilities, setCapabilities] = useState({
        isLoading: true,
        data: null,
        error: null,
    });

    const addonStatusEnabled = isLogged && !isOwnerRole;

    const {
        data: addonStatusData,
        fetchedAt: addonStatusFetchedAt,
        isLoading: addonStatusLoading,
        error: addonStatusError,
        refresh: refreshAddonStatusRaw,
    } = useAddonStatus({ token: addonStatusEnabled ? token : null, isLogged: addonStatusEnabled });

    const refreshAddonStatus = useCallback(
        (options = {}) => {
            if (!token || !addonStatusEnabled) {
                return Promise.resolve({});
            }
            if (typeof options === "boolean") {
                return refreshAddonStatusRaw({ force: options, reason: "compat" });
            }
            return refreshAddonStatusRaw(options);
        },
        [addonStatusEnabled, refreshAddonStatusRaw, token]
    );

    const fetchAddonStatus = useCallback(
        (force = false, reason = "compat") =>
            refreshAddonStatus({ force, reason }),
        [refreshAddonStatus]
    );

    const addonsStatus = useMemo(
        () => ({
            isLoading: addonStatusLoading,
            data: addonStatusData,
            fetchedAt: addonStatusFetchedAt,
            error: addonStatusError,
        }),
        [addonStatusData, addonStatusError, addonStatusFetchedAt, addonStatusLoading]
    );

    /**
     * Función para obtener datos del usuario principal.
     * Si el token indica que es usuario asociado => se usa main_user_id 
     * (u otro campo que tengas) en vez de user_id, 
     * para la llamada a /user/users/:id.
     */
    const fetchUserData = async () => {
        if (isOwnerRole) {
            setInfoUserAuth(prev => ({
                ...prev,
                isLoading: false,
                data: {
                    ...prev.data,
                    role: 'PROPIETARIO',
                    token,
                }
            }));
            return;
        }
        if (!isLogged || !decodedToken) {
            // No estamos logueados o no hay token decodificado
            setInfoUserAuth(prev => ({
                ...prev,
                isLoading: false
            }));
            return;
        }

        // Revisar si en el token tenemos is_associated y main_user_id
        let finalUserId = decodedToken.user_id; // por defecto
        if (decodedToken.is_associated && decodedToken.main_user_id) {
            // Sobrescribimos con ID del principal
            finalUserId = decodedToken.main_user_id;
            console.log("=> Es usuario asociado. Cargamos la data del principal con ID:", finalUserId);
        }

        if (!finalUserId) {
            // Sin user_id => nada que hacer
            setInfoUserAuth(prev => ({
                ...prev,
                isLoading: false
            }));
            return;
        }

        try {
            // Consumimos la API para /user/users/:id => USERS__GET_DETAIL_USER
            // Ajusta según el alias que hayas definido en tu "APISCONSTS" o similar
            const res = await ApiConsume(
                "USERS__GET_DETAIL_USER", 
                'GET', 
                {}, 
                finalUserId, // <-- id real a buscar
                token
            );

            if (res.msg_error) {
                throw new Error(res.msg_error);
            }

            const DATA = res.data || {};
            setInfoUserAuth({
                isLoading: false,
                data: {
                    id: DATA.id,
                    first_name: DATA.first_name || "",
                    last_name: DATA.last_name || "",
                    domicilio: DATA.domicilio || "",
                    telefono: DATA.telefono || "",
                    cuit: DATA.cuit || "",
                    provincia: DATA.provincia || "",
                    codigo_postal: DATA.codigo_postal || "",
                    afip: DATA.afip || "",
                    username: DATA.username || "",
                    email: DATA.email || "",
                    role: DATA.role || "",
                    numero_inscripcion_rca_rcpa: DATA.numero_inscripcion_rca_rcpa || "",
                    telefono_urgencias: DATA.telefono_urgencias || "",
                    token: token,
                    // Asegurarnos de guardar is_superuser
                    is_superuser: DATA.is_superuser || false,
                }
            });

            // Si tienes un formulario para editar perfil, lo llenas:
            setInputProfileUserValue({
                first_name: DATA.first_name || "",
                last_name: DATA.last_name || "",
                domicilio: DATA.domicilio || "",
                telefono: DATA.telefono || "",
                cuit: DATA.cuit || "",
                provincia: DATA.provincia || "",
                codigo_postal: DATA.codigo_postal || "",
                afip: DATA.afip || "",
                username: DATA.username || "",
                email: DATA.email || "",
                role: DATA.role || "",
                numero_inscripcion_rca_rcpa: DATA.numero_inscripcion_rca_rcpa || "",
                telefono_urgencias: DATA.telefono_urgencias || ""
            });

        } catch (error) {
            console.error("Error al obtener datos del usuario (o principal):", error);
            notify('Error al obtener los datos del usuario. Inténtalo nuevamente.', 'error');

            setInfoUserAuth({
                isLoading: false,
                data: {
                    id: 0,
                    first_name: "",
                    last_name: "",
                    domicilio: "",
                    telefono: "",
                    cuit: "",
                    provincia: "",
                    codigo_postal: "",
                    afip: "",
                    username: "",
                    email: "",
                    role: "",
                    numero_inscripcion_rca_rcpa: "",
                    telefono_urgencias: "",
                    token: null,
                    is_superuser: false,
                }
            });
            setInputProfileUserValue({
                first_name: "",
                last_name: "",
                domicilio: "",
                telefono: "",
                cuit: "",
                provincia: "",
                codigo_postal: "",
                afip: "",
                username: "",
                email: "",
                role: "",
                numero_inscripcion_rca_rcpa: "",
                telefono_urgencias: ""
            });
        }
    };

    const fetchCapabilities = useCallback(async () => {
        if (!isLogged || !token) {
            setCapabilities({ isLoading: false, data: null, error: null });
            return;
        }

        setCapabilities((prev) => ({ ...prev, isLoading: true, error: null }));
        try {
            const res = await ApiConsume("AUTH__ME_CAPABILITIES", "GET", {}, null, token);
            if (res.status >= 200 && res.status < 300) {
                setCapabilities({
                    isLoading: false,
                    data: res.data || null,
                    error: null,
                });
            } else {
                setCapabilities({
                    isLoading: false,
                    data: null,
                    error: res.msg_error || "No se pudieron cargar las capabilities.",
                });
            }
        } catch (error) {
            setCapabilities({
                isLoading: false,
                data: null,
                error: error?.message || "No se pudieron cargar las capabilities.",
            });
        }
    }, [isLogged, token]);

    // Llamamos a fetchUserData una vez que sepamos si hay token y qué dice el decodedToken
    useEffect(() => {
        fetchUserData();
    }, [isLogged, decodedToken, token, isOwnerRole]);

    useEffect(() => {
        fetchCapabilities();
    }, [fetchCapabilities]);

    useEffect(() => {
        if (!addonStatusEnabled) return;
        refreshAddonStatus({ force: false, reason: "provider-init" });
    }, [addonStatusEnabled, refreshAddonStatus]);

    /**
     * Ejemplo de función para actualizar perfil (usuario principal).
     */
    const onUpdateProfileUserAuth = async (e) => {
        e.preventDefault();
        try {
            const API = await ApiConsume(
                "USERS__UPDATE_USER", 
                'PUT', 
                useInputProfileUserValue, 
                useInfoUserAuth.data.id, 
                token
            );
            if (API.msg_success) {
                notify(API.msg_success, 'success');
                await fetchUserData(); // Re-fetch para refrescar
            } else {
                notify('Error al actualizar el perfil. Inténtalo nuevamente.', 'error');
            }
        } catch (error) {
            console.error('Error al actualizar el perfil:', error);
            notify('Error al actualizar el perfil. Inténtalo nuevamente.', 'error');
        }
    };

    const canCapability = useCallback(
        (actionKey, fallback = false) => {
            const actions = capabilities?.data?.actions;
            if (!actions || typeof actions[actionKey] === "undefined") {
                // Fallback para migración gradual de lógica legacy.
                return fallback;
            }
            return Boolean(actions[actionKey]);
        },
        [capabilities]
    );

    return (
        <UseDataUserContext.Provider value={{
            decodedToken,
            useInfoUserAuth,
            setInfoUserAuth,
            useInputProfileUserValue,
            setInputProfileUserValue,
            onUpdateProfileUserAuth,
            addonsStatus,
            refreshAddonStatus,
            fetchAddonStatus,
            capabilities,
            fetchCapabilities,
            canCapability,
        }}>
            {children}
        </UseDataUserContext.Provider>
    );
};
